/**
 * Auszahlungs- und Rechnungsdaten des Partners.
 * Inkl. Adresse (Pflicht für Gutschrift) und USt-Status.
 */
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import type { AffiliateRecord } from "@/hooks/useMyAffiliate";

interface PayoutForm {
  payout_account_holder: string;
  payout_iban: string;
  payout_bic: string;
  ust_id: string;
  billing_full_name: string;
  billing_street: string;
  billing_zip: string;
  billing_city: string;
  billing_country: string;
  tax_status: "kleinunternehmer" | "vat" | "";
}

interface Props {
  affiliate: AffiliateRecord;
  userId: string;
}

const empty = (s: string | null | undefined) => (s ?? "").trim();

const PartnerPayoutForm = ({ affiliate, userId }: Props) => {
  const qc = useQueryClient();
  const [form, setForm] = useState<PayoutForm>({
    payout_account_holder: "",
    payout_iban: "",
    payout_bic: "",
    ust_id: "",
    billing_full_name: "",
    billing_street: "",
    billing_zip: "",
    billing_city: "",
    billing_country: "DE",
    tax_status: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      payout_account_holder: empty(affiliate.payout_account_holder),
      payout_iban: empty(affiliate.payout_iban),
      payout_bic: empty(affiliate.payout_bic),
      ust_id: empty(affiliate.ust_id),
      billing_full_name: empty(affiliate.billing_full_name),
      billing_street: empty(affiliate.billing_street),
      billing_zip: empty(affiliate.billing_zip),
      billing_city: empty(affiliate.billing_city),
      billing_country: empty(affiliate.billing_country) || "DE",
      tax_status: (affiliate.tax_status ?? "") as PayoutForm["tax_status"],
    });
  }, [affiliate]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { error } = await supabase
        .from("affiliates")
        .update({
          payout_account_holder: form.payout_account_holder || null,
          payout_iban: form.payout_iban.replace(/\s+/g, "") || null,
          payout_bic: form.payout_bic.replace(/\s+/g, "") || null,
          ust_id: form.ust_id || null,
          billing_full_name: form.billing_full_name || null,
          billing_street: form.billing_street || null,
          billing_zip: form.billing_zip || null,
          billing_city: form.billing_city || null,
          billing_country: form.billing_country || "DE",
          tax_status: form.tax_status || null,
        })
        .eq("id", affiliate.id);
      if (error) throw error;
      toast({ title: "Daten gespeichert" });
      await qc.invalidateQueries({ queryKey: ["my-affiliate", userId] });
    } catch (err) {
      toast({ title: "Fehler", description: (err as Error).message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Auszahlungs- und Rechnungsdaten</CardTitle>
        <CardDescription>
          Für die Auszahlung brauchen wir IBAN und deine vollständige Anschrift (steht auf der Gutschrift). Mindest-Auszahlung: 25 €.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor="full_name">Name / ggf. Firma</Label>
            <Input id="full_name" value={form.billing_full_name} onChange={(e) => setForm({ ...form, billing_full_name: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="street">Straße und Hausnummer</Label>
            <Input id="street" value={form.billing_street} onChange={(e) => setForm({ ...form, billing_street: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="zip">PLZ</Label>
            <Input id="zip" value={form.billing_zip} onChange={(e) => setForm({ ...form, billing_zip: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="city">Ort</Label>
            <Input id="city" value={form.billing_city} onChange={(e) => setForm({ ...form, billing_city: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="country">Land</Label>
            <Input id="country" value="Deutschland" readOnly disabled />
            <p className="mt-1 text-xs text-muted-foreground">
              Das Partnerprogramm ist aktuell nur für in Deutschland steuerpflichtige Partner verfügbar.
            </p>
          </div>

          <div className="sm:col-span-2 rounded-md border p-4 space-y-3">
            <Label>Umsatzsteuer-Status</Label>
            <RadioGroup
              value={form.tax_status}
              onValueChange={(v) => setForm({ ...form, tax_status: v as PayoutForm["tax_status"] })}
            >
              <div className="flex items-start gap-2">
                <RadioGroupItem value="kleinunternehmer" id="tax_klein" className="mt-1" />
                <Label htmlFor="tax_klein" className="font-normal cursor-pointer">
                  Kleinunternehmer nach §19 UStG (keine MwSt-Ausweisung)
                </Label>
              </div>
              <div className="flex items-start gap-2">
                <RadioGroupItem value="vat" id="tax_vat" className="mt-1" />
                <Label htmlFor="tax_vat" className="font-normal cursor-pointer">
                  Umsatzsteuerpflichtig – 19 % sind in der Provision enthalten
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="ust">Steuernummer (optional)</Label>
            <Input id="ust" value={form.ust_id} onChange={(e) => setForm({ ...form, ust_id: e.target.value })} />
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="holder">Kontoinhaber</Label>
            <Input id="holder" value={form.payout_account_holder} onChange={(e) => setForm({ ...form, payout_account_holder: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="iban">IBAN</Label>
            <Input id="iban" value={form.payout_iban} onChange={(e) => setForm({ ...form, payout_iban: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="bic">BIC</Label>
            <Input id="bic" value={form.payout_bic} onChange={(e) => setForm({ ...form, payout_bic: e.target.value })} />
          </div>

          <div className="sm:col-span-2">
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Speichern
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default PartnerPayoutForm;
